import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faAdd,
	faExpand,
} from '@fortawesome/pro-light-svg-icons';
import { Moment } from 'moment';
import {
	CurrencySymbols,
	EstimateStatus,
} from '@shared/utils/constants';
import { calculateTotal } from '@shared/utils/math';
import type { GetFixtureDetailsResponse } from '@api/features/fixtures/getFixtureDetails';
import type { TcFixtureProps } from '@api/models/tc-fixture';
import type { GetCounterpartiesResponse } from '@api/features/counterparties/getCounterparties';
import type { GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import { TcTermsProps } from '@api/models/tc-terms';
import { UpdateTcTermsRequest } from '@api/features/fixtures/updateTcTerms';
import type { FixingCompanyProps } from '@api/models/fixing-company';
import DatePicker from '@client/components/DatePicker';
import NumericInput from '@client/components/NumericInput';
import Select from '@client/components/Select';
import Button from '@client/components/Button';
import getFixingCompanyOptions from '@client/utils/getFixingCompanyOptions';
import styles from '../components/styles/EstimateConfigurationCard.module.css';

const tcEstimateItems = ({
	tcTerms,
	fixtureDetails,
	estimate,
	charterers,
	fixingCompanies,
	setShowBrokersModal,
	onUpdateTotalBrokerCommission,
	setShowTcTermsModal,
	onUpdateTcTerms,
	componentDisabled,
}: {
	fixtureDetails: GetFixtureDetailsResponse<TcFixtureProps>;
	estimate: GetEstimateDetailsResponse | null;
	charterers: GetCounterpartiesResponse | undefined;
	fixingCompanies: Array<FixingCompanyProps>;
	setShowBrokersModal: (open: boolean) => void;
	onUpdateTotalBrokerCommission: (comm: number) => void;
	setShowTcTermsModal: (open: boolean) => void;
	tcTerms: TcTermsProps;
	onUpdateTcTerms: (attributes: UpdateTcTermsRequest['attributes']) => void;
	componentDisabled?: boolean;
}) => {
	const isFixed = estimate?.status === EstimateStatus.Fixed;

	return [
		{
			title: 'Laycan From',
			width: 260,
			render: () => (
				<DatePicker
					data-testid="tc-laycan-from"
					time
					disabled={isFixed}
					variant="borderless"
					value={tcTerms.laycanFrom}
					placeholder="Laycan From"
					onChange={(d) => {
						onUpdateTcTerms({
							laycanFrom: d as Moment,
						});
					}}
				/>
			),
		},
		{
			title: 'Laycan To',
			span: 1,
			width: 260,
			render: () => (
				<DatePicker
					data-testid="tc-laycan-to"
					time
					disabled={isFixed}
					variant="borderless"
					value={tcTerms.laycanTo}
					placeholder="Laycan To"
					onChange={(d) => {
						onUpdateTcTerms({
							laycanTo: d as Moment,
						});
					}}
				/>
			),
		},
		{
			title: 'Gross Ballast Bonus',
			span: 1,
			width: 180,
			render: () => (
				<NumericInput
					data-testid="tc-gross-ballast-bonus"
					disabled={isFixed}
					variant="borderless"
					value={tcTerms.grossBallastBonus}
					addonBefore={
						CurrencySymbols[estimate?.currency as keyof typeof CurrencySymbols] ??
						estimate?.currency
					}
					onChange={(v) => {
						onUpdateTcTerms({
							grossBallastBonus: v,
						});
					}}
				/>
			),
		},
		{
			title: 'Add. comm.',
			span: 1,
			width: 110,
			render: () => (
				<NumericInput
					data-testid="tc-add-comm"
					disabled={isFixed}
					variant="borderless"
					addonBefore="%"
					value={tcTerms.addressCommission}
					onChange={(v) => {
						onUpdateTcTerms({
							addressCommission: v,
						});
					}}
				/>
			),
		},
		{
			title: 'Brokers comm.',
			span: 1,
			width: 150,
			render: () => (
				<NumericInput
					data-testid="tc-brokers-comm"
					disabled={isFixed || (fixtureDetails.brokers.length > 1)}
					variant="borderless"
					addonBefore="%"
					addonAfter={(
						<FontAwesomeIcon
							color={componentDisabled ? 'lightgray' : '#1677ff'}
							className={componentDisabled ? undefined : styles.addIcon}
							icon={faAdd}
							onClick={() => {
								if (componentDisabled) {
									return;
								}

								setShowBrokersModal(true);
							}}
						/>
					)}
					value={
						fixtureDetails?.brokers.length < 2 && estimate?.totalTcBrokerCommission != null ?
							Number(estimate.totalTcBrokerCommission.toFixed(2)) :
							Number(calculateTotal(fixtureDetails.brokers, (b) => b?.commission ?? 0).toFixed(2))
					}
					placeholder="Broker Commission"
					onChange={(e) => onUpdateTotalBrokerCommission(Number(e))}
				/>
			),
		},
		{
			title: 'Charterer',
			span: 1,
			render: () => (
				<Select
					data-testid="tc-charterer"
					disabled={isFixed}
					placeholder="Select charterer"
					value={tcTerms.counterpartyId}
					variant="borderless"
					size="small"
					dropdownStyle={{ width: 200 }}
					showSearch
					options={(charterers ?? []).map((c) => ({
						label: c.name,
						value: c.id,
					}))}
					onChange={(v) => {
						onUpdateTcTerms({
							counterpartyId: v ?? undefined,
						});
					}}
				/>
			),
		},
		{
			title: 'Fixing Company',
			span: 1,
			render: () => (
				<Select
					data-testid="tc-fixing-company"
					disabled={isFixed}
					placeholder="Select Fixing Company"
					value={tcTerms.fixingCompanyId}
					variant="borderless"
					size="small"
					dropdownStyle={{ width: 200 }}
					showSearch
					options={getFixingCompanyOptions(fixingCompanies, tcTerms.fixingCompanyId)}
					onChange={(v) => {
						onUpdateTcTerms({
							fixingCompanyId: v ?? undefined,
						});
					}}
				/>
			),
		},
		{
			title: '',
			align: 'right' as const,
			render: () => (
				<Button
					onClick={() => setShowTcTermsModal(true)}
					icon={(<FontAwesomeIcon icon={faExpand} />)}
				>
					Open TC Terms
				</Button>
			),
		},
	];
};

export default tcEstimateItems;
